import systemMockProducts from "utils/mocks/systemProducts.json";
import liff from "../lib/liff";
import {
  OMAGUN_SOUL_ID,
  createOmagunMockQuestGroups,
} from "constants/omagun";

const useMock = process.env.REACT_APP_USE_MOCK === "true";

const loginChannelId = process.env.REACT_APP_LINE_LOGIN_CHANNEL_ID;

const getIdToken = () => {
  return liff.getIDToken();
};

const timezoneRequest = async (method, path = "", body) => {
  const response = await fetch(`/api/users/timezone${path}`, {
    method,
    headers: { Authorization: `Bearer ${getIdToken()}`, "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw new Error("Timezone request failed");
  return response.json();
};
export const getUserTimezone = () => timezoneRequest("GET");
export const getTimezoneOptions = () => timezoneRequest("GET", "/options");
export const putUserTimezone = (timezone) => timezoneRequest("PUT", "", { timezone });

export const getUserEmail = async () => {
  if (useMock) {
    return { email_address: "mock-user@example.com" };
  }
  const token = getIdToken();

  const response = await fetch(`/api/users/email`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user");
  }

  const data = await response.json();
  return data;
};

export const getUserLineEmail = async () => {
  const token = getIdToken();
  try {
    const response = await fetch("https://api.line.me/oauth2/v2.1/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        id_token: token,
        client_id: loginChannelId,
      }),
    });

    const data = await response.json();

    if ("email" in data) {
      return data.email;
    } else {
      console.warn("email not found in response:", data);
      return "";
    }
  } catch (error) {
    console.error("Failed to fetch email:", error);
    return ""; // 失敗時も空文字を返す
  }
};

// メール配信設定込みのAPI
export const updateUserEmail = async (emailAddress, disableMailDelivery) => {
  const token = getIdToken();
  const response = await fetch(`/api/users/email`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email_address: emailAddress,
      disable_mail_delivery: disableMailDelivery,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to post email address");
  }
  return response.json();
};

export const getUserEpisode = async (soulId) => {
  if (useMock) {
    return {
      eye_catch_url: "https://via.placeholder.com/300x200",
      name: "Mock Soul",
      remaining_days: 7,
      description: "Mock episode description",
      relationship_level: 3,
      is_expired: false,
      can_reset: true,
      has_reset_options: false,
      can_soft_reset: false,
      can_shorten_ticket: false,
      mode_info: {
        can_schedule: false,
        is_scheduled: false,
        mode_after_switch: "normal",
        remaining_days_after_switch: 5,
        switch_at: "2026-04-01T00:00:00Z",
      },
    };
  }
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/episode`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error("Failed to fetch user episode");
  }
  return response.json();
};

// in use
export const listQuests = async (soulId) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/quests`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch quest list");
  }
  return response.json();
};

// in use
export const listQuestGroup = async (soulId) => {
  if (useMock) {
    return [
      {
        quest_group_key: "mock_group_1",
        quest_group_name: "Quest Reward",
        reward_url: null,
        quests: [
          { quest_key: "mock_quest_1", name: "Mock Quest 1", description: "Talk to the soul", is_cleared: true, reward_url: null },
          { quest_key: "mock_quest_2", name: "Mock Quest 2", description: "Complete a challenge", is_cleared: false, reward_url: null },
        ],
      },
    ];
  }
  const token = getIdToken();
  const response = await fetch(`/api/users/souls/${soulId}/quest_groups`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch quest group list");
  }
  return response.json();
};

// in use
export const scheduleModeSwitch = async (soulId) => {
  const token = getIdToken();

  const response = await fetch(
    `/api/users/souls/${soulId}/schedule_mode_switch`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to schedule mode switch");
  }
  return response.json();
};

// in use
export const unscheduleModeSwitch = async (soulId) => {
  const token = getIdToken();

  const response = await fetch(
    `/api/users/souls/${soulId}/unschedule_mode_switch`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to unschedule mode switch");
  }
  return response.json();
};

// in use
export const resetMemory = async (soulId) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/reset_memory`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to reset memory");
  }
  return response.json();
};

export const getMemoryRollbackStatus = async (soulId) => {
  if (useMock) {
    return { can_request: true, cooldown_until: null };
  }
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/rollback/status`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch memory rollback status");
  }
  return response.json();
};

export const getMemoryRollbackCandidates = async (
  soulId,
  { date, timezone } = {}
) => {
  if (useMock) {
    return {
      candidates: [
        {
          candidate_id: "mock-candidate-1",
          text: "星は、まだ動いている。",
          texts: ["星は、まだ動いている。"],
          bubble_count: 1,
          started_at: "2026-06-01T10:00:00",
          created_at: "2026-06-01T10:00:00",
        },
      ],
    };
  }
  const token = getIdToken();
  const params = new URLSearchParams();
  if (date) {
    params.set("date", date);
  }
  if (timezone) {
    params.set("timezone", timezone);
  }
  const query = params.toString();

  const response = await fetch(
    `/api/users/souls/${soulId}/rollback/candidates${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch memory rollback candidates");
  }
  return response.json();
};

export const rollbackMemory = async (
  soulId,
  candidateId,
  { reasonType, reasonText } = {}
) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/rollback`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      candidate_id: candidateId,
      reason_type: reasonType,
      reason_text: reasonText,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to rollback memory");
  }
  return response.json();
};

export const getRequestFormChatCandidates = async (
  soulId,
  { date, timezone, limit = 50 } = {}
) => {
  if (useMock) {
    return {
      candidates: [
        {
          candidate_id: "mock-request-candidate-1",
          text: "星は、まだ動いている。",
          texts: ["星は、まだ動いている。"],
          bubble_count: 1,
          started_at: "2026-06-01T10:00:00",
          created_at: "2026-06-01T10:00:00",
        },
      ],
    };
  }
  const token = getIdToken();
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (date) {
    params.set("date", date);
  }
  if (timezone) {
    params.set("timezone", timezone);
  }

  const response = await fetch(
    `/api/users/souls/${soulId}/request-form/chat-candidates?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch request form chat candidates");
  }
  return response.json();
};

export const submitRequestFormChat = async (
  soulId,
  { candidateId, problemText }
) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/request-form/chat`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      candidate_id: candidateId,
      problem_text: problemText,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to submit request form chat");
  }
  return response.json();
};

export const submitRequestFormOther = async (
  soulId,
  { inquiryText, language }
) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/request-form/other`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      inquiry_text: inquiryText,
      language,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to submit request form other");
  }
  return response.json();
};

export const getQuestClearMessages = async (soulId, { cursor, limit = 30 } = {}) => {
  const token = getIdToken();
  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(limit));
  if (cursor) {
    queryParams.set("cursor", cursor);
  }

  const response = await fetch(
    `/api/users/souls/${soulId}/quest-clear/messages?${queryParams.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch quest clear messages");
  }
  return response.json();
};

export const createQuestClearRequest = async (
  soulId,
  { questKey, situation, reasonText, messageGroupId }
) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/quest-clear`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      quest_key: questKey,
      situation,
      reason_text: reasonText,
      message_group_id: messageGroupId,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to create quest clear request");
  }
  return response.json();
};

export const getQuestClearRequests = async (soulId, { limit = 10 } = {}) => {
  const token = getIdToken();
  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(limit));

  const response = await fetch(
    `/api/users/souls/${soulId}/quest-clear/requests?${queryParams.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch quest clear requests");
  }
  return response.json();
};

// in use
export const listEpisodeTickets = async (soulId) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/episode_tickets`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch episode tickets");
  }

  return response.json();
};

export const listTickets = async (soulId) => {
  if (useMock) {
    // Return mock tickets
    return [
      {
        ticket_key: "mock_ticket_1",
        name: "10日間プラン",
        description: "全てのクエストに挑戦可能",
        price: 1000,
        episode_id: "mock_episode_1",
        stripe_price_id: "price_mock_1",
      },
      {
        ticket_key: "mock_ticket_2",
        name: "3日間プラン",
        description: "一部のクエストに挑戦可能",
        price: 300,
        episode_id: "mock_episode_2",
        stripe_price_id: "price_mock_2",
      },
    ];
  }
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/tickets`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch episode tickets");
  }

  return response.json();
};

// ==============================================
// PayPay related APIs
// ==============================================
export const getUnconfirmedPayPayPayment = async (soulId) => {
  if (useMock) {
    return { merchant_payment_id: null };
  }
  const token = getIdToken();
  const response = await fetch(
    `/api/users/paypay/unconfirmed?soul_id=${soulId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch unconfirmed payment");
  }
  return response.json();
};

export const getPayPayPaymentStatus = async (merchantPaymentId, options = {}) => {
  const normalizePayPayStatus = (statusValue) => {
    const status = String(statusValue || "").toUpperCase();
    if (status === "COMPLETED") {
      return "COMPLETED";
    }
    if (status === "CREATED" || status === "PENDING" || status === "AUTHORIZED") {
      return "CREATED";
    }
    return status || "ERROR";
  };

  const getPath = (opts = {}) => {
    if (opts.useSystemApi && opts.soulId) {
      // 初回購入ではまだprofileがない。チケット購入専用の確認APIで付与まで進める。
      if (opts.ticketKey && opts.purchaseType !== "tamil") {
        return `${systemApiBase}/souls/${opts.soulId}/tickets/${encodeURIComponent(opts.ticketKey)}/purchase/paypay/status/${encodeURIComponent(merchantPaymentId)}`;
      }
      return `${systemApiBase}/souls/${opts.soulId}/payments/paypay/status/${merchantPaymentId}`;
    }
    return `/api/users/paypay/status/${merchantPaymentId}`;
  };

  const token = getIdToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(getPath(options), {
      method: "GET",
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch PayPay payment status");
    }
    const json = await response.json();
    return {
      ...json,
      raw_status: json.status,
      status: normalizePayPayStatus(json.status),
    };
  } finally {
    clearTimeout(timeout);
  }
};

export const createPayPayTicketQrCode = async (
  soulId,
  episodeId,
  ticketKey
) => {
  const token = getIdToken();
  const response = await fetch(`/api/users/paypay/qrcode?soul_id=${soulId}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ episode_id: episodeId, ticket_key: ticketKey }),
  });

  if (!response.ok) {
    throw new Error("Failed to create stripe session");
  }
  return response.json();
};

export const createPayPayQrCode = async (soulId, data) => {
  const token = getIdToken();

  const jsonString = JSON.stringify(data);
  const blob = new Blob([jsonString], { type: "application/json" });

  const formData = new FormData();
  // formData.append("product_type", data.product_type);
  formData.append("data", blob, "payload.json");

  const response = await fetch(
    `/api/users/paypay/v2/qrcode?soul_id=${soulId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error("Failed to create paypay qr code");
  }
  return response.json();
};

// ==============================================
// Stripe related APIs
// ==============================================
export const createStripeTicketCheckoutSession = async (
  soulId,
  episodeId,
  ticketKey
) => {
  const token = getIdToken();
  const response = await fetch(
    `/api/users/stripe/checkout_sessions?soul_id=${soulId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ episode_id: episodeId, ticket_key: ticketKey }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to create stripe session");
  }
  return response.json();
};

export const createStripeCheckoutSession = async (soulId, data) => {
  const token = getIdToken();

  const jsonString = JSON.stringify(data);
  const blob = new Blob([jsonString], { type: "application/json" });

  const formData = new FormData();
  // formData.append("product_type", data.product_type);
  formData.append("data", blob, "payload.json");

  const response = await fetch(
    `/api/users/stripe/v2/checkout_sessions?soul_id=${soulId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error("Failed to create stripe session");
  }
  return response.json();
};

// ==============================================
// Guidelines related APIs
// ==============================================
// モックでは `?mock_guidelines=pending` で未同意（同意の確認が出る状態）にする
const mockGuidelinesAgreed = () =>
  new URLSearchParams(window.location.search).get("mock_guidelines") !== "pending";

export const getGuidelinesAgreementStatus = async (soulId) => {
  if (useMock) {
    return mockGuidelinesAgreed();
  }
  const token = getIdToken();
  const response = await fetch(
    `/api/users/souls/${soulId}/guidelines_agreement`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch guidelines agreement status");
  }
  const parsed = await response.json();
  return parsed.agreed;
};

export const postGuidelinesAgreement = async (soulId) => {
  if (useMock) {
    return { message: "Guidelines agreement registered" };
  }
  const token = getIdToken();
  const response = await fetch(
    `/api/users/souls/${soulId}/guidelines_agreement`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to post guidelines agreement");
  }
  return response.json();
};

// Claim a free ticket for a soul
export const claimFreeTicket = async (soulId, passcode) => {
  const token = getIdToken();
  const response = await fetch(`/api/users/souls/${soulId}/free_ticket`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      passcode: passcode,
    }),
  });
  if (!response.ok) {
    throw new Error("Failed to claim free ticket");
  }
  return response.json();
};

export const claimTestTicket = async (soulId, { password, emailAddress }) => {
  if (useMock) {
    return { message: "Test ticket redeemed", ticket_key: "nazuna_test_10days_202607" };
  }

  const token = getIdToken();
  const response = await fetch(`/api/users/souls/${soulId}/test_ticket`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      password,
      email_address: emailAddress,
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || "Failed to claim test ticket");
  }

  return response.json();
};

// ==============================================
// 個別作品API
// ==============================================
export const getBirthdayPageData = async (soulId) => {
  const token = getIdToken();
  const response = await fetch(`/api/users/souls/${soulId}/birthday`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch birthday page data");
  }
  const parsed = await response.json();
  return parsed;
};

export const createBirthdayReservation = async (
  soulId,
  { message, deliveryTime }
) => {
  const token = getIdToken();
  const response = await fetch(
    `/api/users/souls/${soulId}/birthday/reservations`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ message, delivery_time: deliveryTime }),
    }
  );

  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(parsed.detail || "Failed to create birthday reservation");
    error.status = response.status;
    throw error;
  }
  return parsed;
};

export const getHalloweenPageData = async (soulId) => {
  const token = getIdToken();
  const response = await fetch(`/api/users/souls/${soulId}/halloween`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch halloween page data");
  }
  const parsed = await response.json();
  return parsed;
};

export const getPotionWallet = async (soulId) => {
  if (useMock) {
    return { potions: 3, tamil: 1200 };
  }
  const token = getIdToken();
  const response = await fetch(`/api/users/souls/${soulId}/potion_wallet`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch potion wallet");
  }
  return response.json();
};

// Legacy Chi migration endpoint. This was temporarily needed during the early
// ticket-duration migration and is no longer used from the normal MyPage flow.
export const shortenTicket = async (soulId) => {
  const token = getIdToken();
  const response = await fetch(`/api/users/souls/${soulId}/shorten-ticket`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to shorten ticket");
  }
  return response.json();
};

export const listEpisodes = async (soulId) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soulId}/episodes`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch episode list");
  }
  return response.json();
};

// not in use
export const getEpisodeByKey = async (episodeKey, soulId) => {
  const token = getIdToken();

  const response = await fetch(
    `/api/users/souls/${soulId}/episodes/${episodeKey}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch episode");
  }
  return response.json();
};

export const getLatestQuest = async (soulId) => {
  const token = getIdToken();

  const queryParams = new URLSearchParams({
    soul_id: soulId,
  }).toString();

  const response = await fetch(`/api/quests/latest?${queryParams}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch quest");
  }
  return response.json();
};

export const getQuestById = async (questId) => {
  const token = getIdToken();

  const response = await fetch(`/api/quests/${questId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch quest");
  }
  return response.json();
};

export const listPlans = async (soul_id) => {
  const token = getIdToken();

  const response = await fetch(`/api/users/souls/${soul_id}/plans`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch plans");
  }
  return response.json();
};

export const listMemoriesMonthly = async (soulId, nextPageToken) => {
  const token = getIdToken();
  const searchParams = {
    soul_id: soulId,
  };

  if (nextPageToken) {
    searchParams.next_page_token = nextPageToken;
  }

  const queryParams = new URLSearchParams(searchParams).toString();

  const response = await fetch(`/api/memories/monthly?${queryParams}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch memories");
  }
  return response.json();
};

export const listMemories = async (soulId, month, nextPageToken) => {
  const token = getIdToken();
  const searchParams = {
    soul_id: soulId,
    month: month,
  };

  if (nextPageToken) {
    searchParams.next_page_token = nextPageToken;
  }

  const queryParams = new URLSearchParams(searchParams).toString();

  const response = await fetch(`/api/memories?${queryParams}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch memories");
  }
  return response.json();
};

export const createSubscriptionSession = async (soulId, planKey) => {
  const token = getIdToken();
  const response = await fetch(
    `api/users/stripe/subscription_sessions?soul_id=${soulId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
      body: JSON.stringify({ plan_key: planKey }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to create subscription session");
  }
  return response.json();
};

export const createMenuTappedEvent = async (soulId, menuKey) => {
  const token = getIdToken();
  const response = await fetch("api/menu_tapped_events", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
    body: JSON.stringify({ soul_id: soulId, menu_key: menuKey }),
  });

  if (!response.ok) {
    throw new Error("Failed to create menu tapped event");
  }
  return response.json();
};

export const getOriginalSiteUrl = async (soulId) => {
  const response = await fetch(`api/original_site_url?soul_id=${soulId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to create menu tapped event");
  }
  return response.json();
};

// ==============================================
// Soul System API (v1)
// ==============================================

const systemApiBase = "/api/v1";

const throwApiError = async (response) => {
  const error = new Error("API Error");
  error.status = response.status;
  try {
    error.body = await response.json();
  } catch {}
  throw error;
};

export const getSystemTicketState = async (soulId) => {
  if (useMock) {
    const mockTicket = new URLSearchParams(window.location.search).get("mock_ticket");
    if (mockTicket === "none") {
      const error = new Error("Ticket not found");
      error.status = 404;
      throw error;
    }
    if (mockTicket === "expired") {
      return {
        ticket_key: "3days_first",
        expires_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        is_expired: true,
      };
    }
    // expires_at: 7日後のUTCタイムスタンプ
    const expireAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 19);
    return { ticket_key: "10days_first", expires_at: expireAt, is_expired: false, can_extend: true };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/tickets/state`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const getPotionAssets = async (soulId) => {
  if (useMock) {
    return { tamil: 1200, potions: 3 };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/assets`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const listSystemTickets = async (soulId) => {
  if (useMock) {
    if (systemMockProducts[soulId]) {
      const state = new URLSearchParams(window.location.search).get("mock_ticket");
      const type = state === "none" ? "first" : state === "expired" ? "continue" : "extend";
      return {
        data: systemMockProducts[soulId].tickets
          .filter(ticket => ticket.type === type)
          .map(ticket => ({ ...ticket, display_price: `¥${ticket.price.toLocaleString("ja-JP")}` })),
      };
    }
    if (soulId === OMAGUN_SOUL_ID) {
      const mockTicket = new URLSearchParams(window.location.search).get("mock_ticket");
      const tickets = mockTicket === "expired"
        ? [{ ticket_key: "10days_continue", name: "続けて10日間", description: "記憶を引き継いで交流を続ける", display_price: "¥550", price: 550 }]
        : mockTicket === "none"
        ? [
            { ticket_key: "3days_first", name: "グンマ見学券 3日間", description: "一部のミッションに挑戦可能", display_price: "¥200", price: 200, notice: "※初回限定" },
            { ticket_key: "10days_first", name: "グンマ県民挑戦権 10日間", description: "全てのクエストに挑戦可能", display_price: "¥550", price: 550 },
          ]
        : [{ ticket_key: "7days_extend", name: "7日間延長", description: "現在の交流期間を延長する", display_price: "¥350", price: 350 }];
      return { data: tickets };
    }
    return {
      data: [
        {
          ticket_key: "plan_10days",
          name: "10日間プラン",
          description: "全てのクエストに挑戦可能",
          price: 1000,
        },
        {
          ticket_key: "plan_3days",
          name: "3日間プラン",
          description: "一部のクエストに挑戦可能",
          price: 300,
        },
      ],
    };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/tickets`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  const json = await response.json();
  return { data: json.tickets || [] };
};

export const listSystemQuests = async (soulId) => {
  if (useMock) {
    if (systemMockProducts[soulId]) {
      const all = new URLSearchParams(window.location.search).get("mock_cleared") === "all";
      return { data: systemMockProducts[soulId].groups.map(group => ({ ...group, quests: group.quests.map((quest, index) => ({ ...quest, is_cleared: all || index === 0 })) })) };
    }
    if (soulId === OMAGUN_SOUL_ID) {
      return { data: createOmagunMockQuestGroups() };
    }
    return {
      data: [
        {
          quest_group_key: "main_quests",
          quest_group_name: null,
          reward_url: null,
          quests: [
            {
              quest_key: "customer",
              quest_title: "顧客",
              description: "ポーションを購入して常連客になろう",
              eye_catch_url: `${process.env.PUBLIC_URL}/potion/quest_customer.jpg`,
              is_cleared: false,
              is_expired: false,
              reward_url: null,
            },
            {
              quest_key: "bonus",
              quest_title: "おまけ",
              description: "特別なイベントに参加しよう",
              eye_catch_url: `${process.env.PUBLIC_URL}/potion/quest_omake.jpg`,
              is_cleared: false,
              is_expired: false,
              reward_url: null,
            },
            {
              quest_key: "high_potion",
              quest_title: "ハイポーション",
              description: "ハイポーションを作ろう",
              eye_catch_url: `${process.env.PUBLIC_URL}/potion/quest_hipotion.jpg`,
              is_cleared: false,
              is_expired: false,
              reward_url: null,
            },
            {
              quest_key: "friend",
              quest_title: "友達",
              description: "カエデと友達になろう",
              eye_catch_url: `${process.env.PUBLIC_URL}/potion/quest_friend.jpg`,
              is_cleared: false,
              is_expired: false,
              reward_url: null,
            },
          ],
        },
      ],
    };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/quests`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  const json = await response.json();
  return { data: json.quest_groups || [] };
};

export const getSystemUnconfirmedPayment = async (soulId) => {
  if (useMock) {
    return { unconfirmed_payment: null };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/payments/unconfirmed`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};


// 使い方ページのガイドライン同意。チケットが無いうちは agreed: null（使い方はいつでも見られる）
export const getSystemGuidelinesAgreement = async (soulId) => {
  if (useMock) {
    return mockGuidelinesAgreed();
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/guidelines-agreement`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  const json = await response.json();
  return json.agreed;
};

export const postSystemGuidelinesAgreement = async (soulId) => {
  if (useMock) {
    return { agreed: true };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/guidelines-agreement`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const purchasePotionTamilStripe = async (soulId, amount) => {
  if (useMock) {
    return { session_id: "cs_mock_xxx" };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/tamil/purchase/stripe`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({ amount }),
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const purchasePotionTamilPaypay = async (soulId, amount) => {
  if (useMock) {
    return {
      url: "https://qr.paypay.ne.jp/mock",
      merchant_payment_id: "mock-payment-id",
    };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/tamil/purchase/paypay`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({ amount }),
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const purchaseSystemTicketStripe = async (
  soulId,
  ticketKey,
  expectedPrice
) => {
  if (useMock) {
    return { session_id: "cs_mock_ticket_xxx" };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/tickets/${ticketKey}/purchase/stripe`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({ expected_price: expectedPrice, currency: "JPY" }),
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const purchaseSystemTicketPaypay = async (
  soulId,
  ticketKey,
  expectedPrice
) => {
  if (useMock) {
    return {
      url: "https://qr.paypay.ne.jp/mock-ticket",
      merchant_payment_id: "mock-ticket-payment-id",
    };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/tickets/${ticketKey}/purchase/paypay`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({ expected_price: expectedPrice, currency: "JPY" }),
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const payPotionTamil = async (soulId, amount) => {
  if (useMock) {
    return { remaining_tamil: 1200 - amount };
  }
  const token = getIdToken();
  const response = await fetch(
    `${systemApiBase}/souls/${soulId}/payments/tamil`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({ amount }),
    }
  );
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const updateSystemUserEmail = async (emailAddress, disableMailDelivery) => {
  if (useMock) {
    return { email_address: emailAddress, mail_delivery_enabled: true };
  }
  const token = getIdToken();
  const response = await fetch(`${systemApiBase}/user/email`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email_address: emailAddress,
      disable_mail_delivery: disableMailDelivery,
    }),
  });
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const getSystemUserEmail = async () => {
  if (useMock) {
    return { email_address: "mock@example.com", mail_delivery_enabled: true };
  }
  const token = getIdToken();
  const response = await fetch(`${systemApiBase}/user/email`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

/**
 * SoulChat Web へ移るための引換券を取得する。
 *
 * LINE の ID Token をそのまま遷移先の URL に載せてはいけない（履歴・リファラ・共有
 * 経路で漏れる。ID Token は LINE Login channel の資産で寿命も長い）。backend が
 * 60 秒・使い捨ての引換券に変換した URL を返すので、それを開く。
 *
 * 引き換えても human_id は変わらないので、**LINE でのこれまでの会話がそのまま
 * SoulChat Web で見える**。
 */
export const createSystemWebHandoff = async (soulId) => {
  if (useMock) {
    return {
      url: `https://stg.soulchat.jp/auth/handoff?t=mock-ticket-${soulId}`,
      expires_at: new Date(Date.now() + 60 * 1000).toISOString(),
    };
  }
  const token = getIdToken();
  const response = await fetch(`${systemApiBase}/auth/line/handoff`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ soul_id: soulId }),
  });
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

/** Initialリッチメニューの導線からだけ、独立した新しいLINE humanを開始する。 */
export const restartSystemLineHuman = async (soulId) => {
  if (useMock) return { human_id: "mock-restarted-human", soul_id: soulId, status: "restarted" };
  const response = await fetch(`${systemApiBase}/auth/line/restart`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getIdToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ soul_id: soulId }),
  });
  if (!response.ok) await throwApiError(response);
  return response.json();
};

/**
 * SoulChat Web の場所（作中の BAR など）で交換した LINE を、いまの LINE に結ぶ。
 *
 * 引換券は Web の会員が受け取った使い捨てのもの（`POST /souls/{id}/line-link`）。
 * LINE の ID トークンが「この LINE」を示す。応答の `add_friend_url` で友だち追加へ進む。
 * 同じ LINE が別の記録に結ばれていれば 409（自動で統合しない）。
 */
export const linkSystemLine = async (ticket) => {
  if (useMock) return { soul_id: "mock_soul", add_friend_url: "https://line.me/R/ti/p/@mock" };
  const response = await fetch(`${systemApiBase}/auth/line/link`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getIdToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify({ ticket }),
  });
  if (!response.ok) await throwApiError(response);
  return response.json();
};

/**
 * LINE のリッチメニューから、SoulChat Web の場所（BAR）へ同じ相手として入る URL。
 * 結んだ会員には 60 秒・使い捨ての通行券を付ける。`to` は Web で開く画面。
 */
// os: liff.getOS() の値（ios / android / web）。送るとアプリを使っている会員には open: "app" が返る。
export const createSystemWebPass = async (soulId, to, os) => {
  if (useMock) return { url: `https://stg.soulchat.jp/auth/pass?soul_id=${soulId}&to=${to}`, pass: false, open: "web" };
  const response = await fetch(`${systemApiBase}/auth/line/web-pass`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getIdToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify(os ? { soul_id: soulId, to, os } : { soul_id: soulId, to }),
  });
  if (!response.ok) await throwApiError(response);
  return response.json();
};

// Backward-compatible aliases for older Potion imports. New system works must
// use the work-neutral names above.
export const getPotionTicketState = getSystemTicketState;
export const listPotionTickets = listSystemTickets;
export const listPotionQuests = listSystemQuests;
export const getPotionUnconfirmedPayment = getSystemUnconfirmedPayment;
export const purchasePotionTicketStripe = purchaseSystemTicketStripe;
export const purchasePotionTicketPaypay = purchaseSystemTicketPaypay;
